import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Globe2, Store, Home, UsersRound, CalendarRange } from 'lucide-react';
import { AdminGeoMapCanvas } from '../AdminGeoMapCanvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cardStyle = {
  background: '#FFFFFF',
  borderRadius: 18,
  padding: 14,
  border: '1px solid rgba(112, 63, 246, 0.08)',
  boxShadow: 'var(--premium-shadow-nested)',
};

// Role palette: matches the colours already used elsewhere in the admin
// panel so admins build a consistent mental map (purple = venue, orange
// = host, blue = guest — same as the Accounts tab action buttons).
// `host_early` uses an amber ring to distinguish "waiting for a venue"
// listings from "active" hosts at a glance.
const ROLE_COLOR = {
  venue: { solid: '#7036F2', soft: 'rgba(112, 63, 246, 0.10)', text: '#2F145F', label: 'Venues' },
  host:  { solid: '#FF9500', soft: 'rgba(255, 149, 0, 0.12)',  text: '#7A4A00', label: 'Hosts'  },
  host_early: { solid: '#F5B300', soft: 'rgba(245, 179, 0, 0.18)', text: '#7A5200', label: 'Early hosts' },
  guest: { solid: '#007AFF', soft: 'rgba(0, 122, 255, 0.10)',  text: '#0F4FA3', label: 'Guests' },
};

const TONE_FOR_KIND = { venue: 'venue', host: 'pending', host_early: 'early', guest: 'info' };

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const pad2 = (n) => String(n).padStart(2, '0');

// Build the list of period options the admin can pick from:
//   • Last 30 days (default)
//   • Each of the trailing 12 months by name (e.g. "January 2026")
//   • Each calendar year going back to 2024 (the launch year)
// Each option carries a stable `code`, the dropdown `label`, and the
// `start`/`end` ISO date strings that get forwarded to the backend.
const buildPeriodOptions = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth(); // 0..11

  const options = [{ code: 'last_30', label: 'Last 30 days', start: null, end: null }];

  // 12 most recent months (oldest -> newest). Each is rendered as a
  // single option in the dropdown so the admin can pick "January 2026"
  // and only see signups from that month.
  for (let i = 0; i < 12; i += 1) {
    let m = month - i;
    let y = year;
    while (m < 0) { m += 12; y -= 1; }
    const startStr = `${y}-${pad2(m + 1)}-01`;
    // End is the first day of the NEXT month (exclusive). Handles year
    // rollover when m == 11.
    const endY = m === 11 ? y + 1 : y;
    const endM = m === 11 ? 0 : m + 1;
    const endStr = `${endY}-${pad2(endM + 1)}-01`;
    options.push({
      code: `month-${y}-${pad2(m + 1)}`,
      label: `${MONTH_NAMES[m]} ${y}`,
      start: startStr,
      end: endStr,
    });
  }

  // Calendar years going back to the project launch (2024). Lets the
  // admin grab the whole year's signup story in one click.
  for (let y = year; y >= 2024; y -= 1) {
    options.push({
      code: `year-${y}`,
      label: `All of ${y}`,
      start: `${y}-01-01`,
      end: `${y + 1}-01-01`,
    });
  }

  return options;
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = Date.now() - t;
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
};

// Map analytics geo-signup rows -> AdminGeoMapCanvas items, with
// role-coloured marker + popup title/subtitle (the previous version
// passed neither, so dots rendered invisible and popups were empty).
const toMapItem = (row, idx) => {
  const palette = ROLE_COLOR[row.kind] || ROLE_COLOR.venue;
  const kindSingularMap = {
    venue: 'Venue',
    host: 'Host',
    host_early: 'Early host',
    guest: 'Guest',
  };
  const singular = kindSingularMap[row.kind] || palette.label.slice(0, -1);
  return {
    id: `${row.kind}-${row.id || idx}`,
    kind: row.kind,
    title: row.label || palette.label,
    subtitle: row.city
      ? `${singular} · ${row.city} · joined ${formatRelative(row.joined_at)}`
      : `${singular} · joined ${formatRelative(row.joined_at)}`,
    badgeText: singular.toUpperCase(),
    statusTone: TONE_FOR_KIND[row.kind] || 'neutral',
    markerColor: palette.solid,
    latitude: row.lat,
    longitude: row.lng,
  };
};

const KIND_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'venue', label: 'Venues' },
  { id: 'host', label: 'Active hosts' },
  { id: 'host_early', label: 'Early hosts' },
];

const LegendDot = ({ kind }) => {
  const palette = ROLE_COLOR[kind];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 9999, background: palette.soft, color: palette.text, fontSize: 11, fontWeight: 800, letterSpacing: 0.3 }}>
      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 9999, background: palette.solid }} />
      {palette.label}
    </span>
  );
};

const FilterChip = ({ filter, active, count, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(filter.id)}
    data-testid={`geo-signups-filter-${filter.id}`}
    aria-pressed={active}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '7px 12px',
      borderRadius: 9999,
      border: `1px solid ${active ? '#0F1115' : '#E5E7EB'}`,
      background: active ? '#0F1115' : '#FFFFFF',
      color: active ? '#FFFFFF' : '#0F1115',
      fontWeight: 700,
      fontSize: 12,
      cursor: 'pointer',
      transition: 'background 140ms ease, border-color 140ms ease',
      fontFamily: 'inherit',
    }}
  >
    {filter.id === 'venue' ? <Store size={12} /> : filter.id === 'host' ? <Home size={12} /> : <Globe2 size={12} />}
    {filter.label}
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '2px 8px', borderRadius: 9999, background: active ? 'rgba(255,255,255,0.18)' : '#F6F7F9', color: active ? '#FFFFFF' : '#0F1115', fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>
      {count}
    </span>
  </button>
);

export const GeoSignupsMap = ({ headers, payload: payloadProp, loading: loadingProp }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [periodCode, setPeriodCode] = useState('last_30');
  const [payload, setPayload] = useState(payloadProp || null);
  // The map starts in fetched-state when we manage our own data (headers
  // provided). When the parent still passes payload directly we trust
  // its `loading` prop instead.
  const [loading, setLoading] = useState(headers ? true : Boolean(loadingProp));

  const periodOptions = useMemo(() => buildPeriodOptions(), []);
  const activePeriod = useMemo(
    () => periodOptions.find((opt) => opt.code === periodCode) || periodOptions[0],
    [periodOptions, periodCode],
  );

  // Self-fetch whenever the admin changes the period. We only do this
  // when the parent passed `headers` — otherwise we stay in legacy mode
  // where the parent provides `payload` directly.
  const fetchGeo = useCallback(async () => {
    if (!headers) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activePeriod.start && activePeriod.end) {
        params.set('start', activePeriod.start);
        params.set('end', activePeriod.end);
      } else {
        params.set('days', '30');
      }
      const res = await axios.get(`${API}/admin/analytics/geo-signups?${params.toString()}`, { headers });
      setPayload(res.data);
    } catch (err) {
      // Soft-fail: keep the previous payload on screen so the map
      // doesn't flicker to empty on a transient network blip.
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('geo-signups fetch failed', err);
      }
    } finally {
      setLoading(false);
    }
  }, [activePeriod, headers]);

  useEffect(() => {
    fetchGeo();
  }, [fetchGeo]);

  // Backward-compat: callers used to pass `items={[...]}`. The richer
  // backend now returns `{items, guests_without_location, by_kind}`,
  // and the parent panel forwards the whole payload as `payload`.
  const items = useMemo(() => (Array.isArray(payload) ? payload : payload?.items) || [], [payload]);
  const guestsWithoutLocation = !Array.isArray(payload) ? (payload?.guests_without_location || 0) : 0;

  const filteredRows = useMemo(() => (
    activeFilter === 'all' ? items : items.filter((row) => row.kind === activeFilter)
  ), [items, activeFilter]);

  const mapItems = useMemo(() => filteredRows.map(toMapItem), [filteredRows]);

  const counts = useMemo(() => ({
    all: items.length,
    venue: items.filter((r) => r.kind === 'venue').length,
    host: items.filter((r) => r.kind === 'host').length,
    host_early: items.filter((r) => r.kind === 'host_early').length,
  }), [items]);

  // Friendly copy for the subhead: "January 2026", "All of 2025", etc.
  const periodSubhead = useMemo(() => {
    if (activePeriod.code === 'last_30') return 'Last 30 days';
    return activePeriod.label;
  }, [activePeriod]);

  return (
    <div style={cardStyle} data-testid="geo-signups-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Globe2 size={18} color="var(--accent-purple-strong)" />
        <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, margin: 0, color: '#000', flex: 1 }}>Where new signups are coming from</h3>
        <span data-testid="geo-signups-count" style={{ fontSize: 12, color: '#666' }}>
          {mapItems.length} pin{mapItems.length === 1 ? '' : 's'}
        </span>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7280' }}>
        {periodSubhead} · venues are placed at their map pin, hosts at their Airbnb listing.
      </p>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }} data-testid="geo-signups-filter-row">
        {KIND_FILTERS.map((filter) => (
          <FilterChip
            key={filter.id}
            filter={filter}
            active={activeFilter === filter.id}
            count={counts[filter.id] || 0}
            onSelect={setActiveFilter}
          />
        ))}

        {/* Period filter — let admins scope the signup map by exact
            month or year so they can audit history cleanly. Sits on
            the right of the kind chips with a calendar icon so it
            reads as a time filter, not a role filter. */}
        <label
          htmlFor="geo-signups-period"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginLeft: 'auto',
            padding: '6px 10px 6px 10px',
            borderRadius: 9999,
            border: '1px solid #E5E7EB',
            background: '#FFFFFF',
            color: '#0F1115',
            fontWeight: 700,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          <CalendarRange size={13} color="#6B7280" />
          <span style={{ color: '#6B7280', fontWeight: 600 }}>Period</span>
          <select
            id="geo-signups-period"
            value={periodCode}
            onChange={(e) => setPeriodCode(e.target.value)}
            data-testid="geo-signups-period-select"
            disabled={!headers}
            style={{
              border: 'none',
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 700,
              color: '#0F1115',
              cursor: headers ? 'pointer' : 'not-allowed',
              outline: 'none',
              padding: '0 4px',
            }}
          >
            {periodOptions.map((opt) => (
              <option key={opt.code} value={opt.code}>{opt.label}</option>
            ))}
          </select>
        </label>
      </div>

      {guestsWithoutLocation > 0 ? (
        <div
          data-testid="geo-signups-guests-without-location"
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 12, background: ROLE_COLOR.guest.soft, color: ROLE_COLOR.guest.text, fontSize: 12, fontWeight: 600, marginBottom: 10 }}
        >
          <UsersRound size={14} />
          {guestsWithoutLocation} new guest{guestsWithoutLocation === 1 ? '' : 's'} signed up too — guests don't share a location so they can't be plotted here.
        </div>
      ) : null}

      {loading && items.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999', padding: 30, fontSize: 13 }}>Loading map…</p>
      ) : items.length === 0 ? (
        <p data-testid="geo-signups-empty-state" style={{ textAlign: 'center', color: '#999', padding: 30, fontSize: 13 }}>
          No new venues or hosts in this period.
        </p>
      ) : (
        <>
          <AdminGeoMapCanvas
            dataTestId="geo-signups-canvas"
            items={mapItems}
            autoSelectFirst={false}
            emptyCopy={activeFilter === 'all' ? 'No new signups to plot yet.' : `No ${activeFilter === 'venue' ? 'venues' : activeFilter === 'host_early' ? 'early hosts' : 'hosts'} match this filter — switch filter to see other roles.`}
            height={360}
            sticky={false}
            title=""
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }} data-testid="geo-signups-legend">
            <LegendDot kind="venue" />
            <LegendDot kind="host" />
            <LegendDot kind="host_early" />
          </div>
          {counts.host_early > 0 ? (
            <p data-testid="geo-signups-early-hint" style={{ margin: '10px 2px 0', fontSize: 12, color: ROLE_COLOR.host_early.text, background: ROLE_COLOR.host_early.soft, padding: '8px 12px', borderRadius: 12, fontWeight: 600 }}>
              {counts.host_early} early host listing{counts.host_early === 1 ? '' : 's'} in this period {counts.host_early === 1 ? 'is' : 'are'} waiting for a venue within 20 km &mdash; that&apos;s where to open next.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};
